<script>
import Layout from '@layouts/main'
import { cdmrMethods, authComputed } from '@state/helpers'
import _ from 'lodash'

export default {
  metaInfo: {
    title: 'CDM-Rs',
    meta: [{ name: 'description', content: 'CDM-Rs' }],
  },
  components: { Layout },
  props: {
    fields: {
      type: Array,
      required: true,
    },
    queryListType: {
      type: String,
      required: false,
      default: 'inbox',
    },
  },
  data() {
    return {
      statusFilter: 0,
      isWorking: false,
      actionItems: [
        { text: 'Approve', value: 0 },
        { text: 'Deny', value: 1 },
        { text: 'Edit', value: 2 },
      ],
      statusItems: [
        { text: 'All', value: null },
        { text: 'Pending', value: 0 },
        { text: 'Finalized', value: 1 },
        { text: 'Exported', value: 2 },
        { text: 'Denied', value: 3 },
      ],
      loadingError: null,
      loading: true,
      pagination: {
        rowsPerPage: 25,
        sortBy: 'created',
        descending: true,
      },
      items: [],
      totalData: 0,
      selectAll: false,
      listType: this.queryListType,
    }
  },
  computed: {
    ...authComputed,
    idField() {
      var ids = this.fields.filter(x => x.uniqueIdentifier === true)
      if (ids) {
        return ids[0]
      }

      return null
    },
    headers() {
      return [
        { text: '', value: '', sortable: false },
        {
          text: this.idField
            ? `${this.idField.caption || this.idField.name}`
            : 'Tracking ID',
          value: 'trackingId',
          sortable: false,
        },
        { text: 'Request Date', value: 'created' },
        { text: 'Type', value: 'cdmrType' },
        { text: 'Status', value: 'status' },
        { text: 'Request By', value: 'requestedBy' },
        { text: 'Mnemonic', value: 'mnemonic' },
        { text: 'Old Description', value: 'oldDesc' },
        { text: 'New Description', value: 'newDesc' },
        { text: 'Old Price', value: 'oldPrice' },
        { text: 'New Price', value: 'newPrice' },
        { text: 'Old CPT', value: 'oldCPT' },
        { text: 'New CPT', value: 'newCPT' },
        { text: 'Old Rev CD', value: 'oldRevCD' },
        { text: 'New Rev CD', value: 'newRevCD' },
      ]
    },
  },
  watch: {
    pagination: {
      handler() {
        this.getDataFromApi()
      },
      deep: true,
    },
    statusFilter: {
      handler() {
        this.getDataFromApi()
      },
    },
    selectAll: {
      handler() {},
    },
  },
  created() {
    this.getDataFromApi = _.debounce(this.getDataFromApi, 500)
  },
  methods: {
    ...cdmrMethods,
    goToCdmr(cdmr) {
      this.$router.push({ name: 'editCdmr', params: { cdmrId: cdmr.id } })
    },
    getDataFromApi() {
      this.loading = true
      var vm = this

      if (this.listType === 'all') {
        return this.getOnlyCdmrs({
          force: true,
          queryTerms: { ...this.pagination, status: this.statusFilter },
        })
          .then(data => {
            vm.loading = false

            vm.totalData = data.totalCount
            vm.items = data.results
          })
          .catch(error => {
            vm.loading = false
            vm.loadingError = error
          })
      } else {
        return this.getInbox({
          force: true,
          queryTerms: { ...this.pagination },
        })
          .then(data => {
            vm.loading = false

            vm.totalData = data.totalCount
            vm.items = data.results
          })
          .catch(error => {
            vm.loading = false
            vm.loadingError = error
          })
      }
    },
    tryExportCdmrs() {
      this.isWorking = true
      return this.scriptCdmrs({ token: this.currentUser.token }).finally(() => {
        this.isWorking = false
      })
    },
    statusStr(statusCode) {
      switch (statusCode) {
        case 0:
          return 'Pending'
        case 1:
          return 'Finalized'
        case 2:
          return 'Exported'
        case 3:
          return 'Denied'
      }
    },
    typeStr(typeCode) {
      switch (typeCode) {
        case 0:
          return 'Add'
        case 1:
          return 'Change'
        case 2:
          return 'Purge'
      }
    },
    listTypeChanged(value) {
      this.$router.replace({ name: 'cdmrs', query: { type: value } })
    },
  },
}
</script>

<template>
  <Layout>
    <v-container fluid grid-list-md ma-0>
      <!-- Header -->
      <v-layout row align-center>
        <v-flex md2>
          <h3>Requests</h3>
        </v-flex>

        <v-flex md8>
          <v-radio-group v-model="listType" row @change="listTypeChanged">
            <v-radio label="My Requests" value="inbox" color="cyan"></v-radio>
            <v-radio label="All Requests" value="all" color="cyan"></v-radio>
          </v-radio-group>
        </v-flex>

        <v-flex md2 text-right>
          <base-link>
            <img
              src="/assets/icon-export.svg"
              :class="$style.export"
              alt="Export"
            />
            Export All
          </base-link>
        </v-flex>
      </v-layout>

      <!-- Table card -->
      <v-card>
        <!-- Table actions -->
        <v-card-title>
          <v-layout justify-space-between>
            <v-flex md7>
              <v-layout align-center>
                <v-flex md2 class="pl-3">
                  # selected
                </v-flex>

                <v-select
                  class="ml-3 mr-5"
                  :class="$style.actions"
                  :items="actionItems"
                  label="Choose Action"
                  color="cyan"
                ></v-select>

                <strong class="mx-1">CDM - Rs To</strong>
                <v-text-field :class="$style.field" color="cyan"
                  >#</v-text-field
                >
                <strong class="mx-1">Priority Items</strong>
                <v-text-field :class="$style.field" color="cyan"
                  >#</v-text-field
                >
              </v-layout>
            </v-flex>

            <v-flex md3>
              <v-text-field
                append-icon="search"
                label="Search"
                color="cyan"
              ></v-text-field>
            </v-flex>
          </v-layout>

          <!-- <v-select
            v-model="statusFilter"
            :items="statusItems"
            label="Status Filter"
          ></v-select>-->

          <!-- <BaseButton :disabled="isWorking" @click="tryExportCdmrs">
            <BaseIcon v-if="isWorking" name="sync" spin />
            <span v-else>Export Finalized CDM-Rs</span>
          </BaseButton>-->
        </v-card-title>

        <!-- Data table -->
        <v-data-table
          :headers="headers"
          :items="items"
          :pagination.sync="pagination"
          :total-items="totalData"
          :loading="loading"
          class="elevation-1"
          select-all
        >
          <v-progress-linear
            slot="progress"
            color="blue"
            indeterminate
          ></v-progress-linear>
          <template slot="items" slot-scope="props">
            <td>
              <v-checkbox
                v-model="selectedTasks"
                :value="item"
                color="cyan"
                hide-details
              />
            </td>
            <td @click="goToCdmr(props.item)">
              <span v-if="idField">{{
                props.item.original[idField.name]
              }}</span>
              <span v-else>{{ props.item.id }}</span>
            </td>
            <td @click="goToCdmr(props.item)">
              <div v-if="props.item.createdBy">
                <span>
                  {{ props.item.createdBy.firstName }}
                  {{ props.item.createdBy.lastName }}
                </span>
              </div>
            </td>
            <td @click="goToCdmr(props.item)">
              {{ props.item.created | moment('MM-DD-YYYY h:mm:ss a') }}
            </td>
            <td @click="goToCdmr(props.item)">
              {{ typeStr(props.item.cdmrType) }}
            </td>
            <td @click="goToCdmr(props.item)">
              {{ statusStr(props.item.status) }}
            </td>
          </template>
        </v-data-table>
      </v-card>
    </v-container>
  </Layout>
</template>

<style lang="scss" module>
@import '@design';

.actions {
  max-width: 141px;
}

.export {
  margin-right: 3px;
  vertical-align: sub;
}

.field {
  max-width: 40px;
}
</style>
